import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './Lightbox.css';
import { FaSearchPlus, FaSearchMinus, FaUndo, FaTimes } from 'react-icons/fa';

class Lightbox extends Component {
  static propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    imageSrc: PropTypes.string,
    altText: PropTypes.string,
  };

  static defaultProps = {
    imageSrc: '',
    altText: 'Image',
  };

  constructor(props) {
    super(props);
    this.state = {
      scale: 1,
      translateX: 0,
      translateY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
    };
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
  }

  handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.handleClose();
    }
  };

  handleMouseDown = (e) => {
    if (this.state.scale > 1) {
      e.preventDefault();
      this.setState({
        isDragging: true,
        startX: e.clientX - this.state.translateX,
        startY: e.clientY - this.state.translateY,
      });
    }
  };

  handleMouseMove = (e) => {
    if (!this.state.isDragging) return;
    e.preventDefault();
    this.setState({
      translateX: e.clientX - this.state.startX,
      translateY: e.clientY - this.state.startY,
    });
  };

  handleMouseUp = () => {
    if (this.state.isDragging) {
      this.setState({ isDragging: false });
    }
  };

  handleZoomIn = (e) => {
    e.stopPropagation();
    this.setState((prevState) => ({
      scale: Math.min(prevState.scale + 0.25, 4),
    }));
  };

  handleZoomOut = (e) => {
    e.stopPropagation();
    this.setState((prevState) => {
      const newScale = Math.max(prevState.scale - 0.25, 0.5);
      // Reset position if zooming out to 1 or less
      if (newScale <= 1) {
        return { scale: newScale, translateX: 0, translateY: 0 };
      }
      return { scale: newScale };
    });
  };

  handleReset = (e) => {
    e.stopPropagation();
    this.setState({ scale: 1, translateX: 0, translateY: 0 });
  };

  handleClose = () => {
    this.setState({ scale: 1, translateX: 0, translateY: 0, isDragging: false });
    this.props.onClose();
  };

  render() {
    const { isOpen, imageSrc, altText } = this.props;
    const { scale, translateX, translateY, isDragging } = this.state;

    if (!isOpen) return null;

    return (
      <div
        className={s.lightboxOverlay}
        onClick={this.handleClose}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.handleClose(); }}
        role="button"
        tabIndex={0}
      >
        <button type="button" className={s.closeButton} onClick={this.handleClose} aria-label="Close">
          <FaTimes />
        </button>

        <div className={s.lightboxContent} onClick={(e) => e.stopPropagation()} role="presentation">
          <img
            src={imageSrc}
            alt={altText}
            className={s.lightboxImage}
            style={{
              transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              transition: isDragging ? 'none' : 'transform 0.2s ease-out',
              userSelect: 'none'
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={this.handleMouseDown}
            onDragStart={(e) => e.preventDefault()}
          />
        </div>

        <div className={s.controlsContainer} onClick={(e) => e.stopPropagation()} role="toolbar" aria-label="Zoom controls">
          <button type="button" className={s.controlButton} onClick={this.handleZoomOut} title="Zoom Out" aria-label="Zoom Out">
            <FaSearchMinus />
          </button>
          <button type="button" className={s.controlButton} onClick={this.handleReset} title="Reset" aria-label="Reset Zoom">
            <FaUndo />
          </button>
          <button type="button" className={s.controlButton} onClick={this.handleZoomIn} title="Zoom In" aria-label="Zoom In">
            <FaSearchPlus />
          </button>
        </div>
      </div>
    );
  }
}

export default withStyles(s)(Lightbox);
